import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchClient } from "./fetchClient";
import { toastSuccess, toastError } from "@/lib/Toast";

// ================ GET (list)

export const useGet = (key, url, params = {}, options = {}) => {
  const searchString = new URLSearchParams(params).toString();
  const separator = url.includes('?') ? '&' : '?';
  const finalUrl = searchString ? `${url}${separator}${searchString}` : url;
  return useQuery({
    queryKey: [key, finalUrl],
    queryFn: () => fetchClient(finalUrl),
    ...options,
  });
};

// ================ GET by ID
export const useGetById = (key, url, id, options = {}, suffix = "") =>
  useQuery({
    queryKey: [key, id, suffix],
    queryFn: () => fetchClient(`${url}/${id}${suffix}`),
    enabled: !!id,
    ...options,
  });

// A caller-supplied `mutationFn` wins over the default request, so hooks can
// wrap the body (e.g. `submitRequisition` adding `isSubmitted: true`).
const baseMutationOptions = (options, successMessage, failureMessage) => ({
  ...(options.mutationFn ? { mutationFn: options.mutationFn } : {}),
  onSuccess: (data, variables, context) => {
    if (options.showSuccessToast !== false) {
      // toastSuccess({
      //   header: data.message || successMessage,
      //   description: data.description || "",
      // });
    }
    if (options.onSuccess) {
      options.onSuccess(data, variables, context);
    }
  },
  onError: (error, variables, context) => {
    try {
      const errorData = JSON.parse(error.message);        
      
      toastError({
        header: errorData.message || failureMessage,
        description: errorData.description || "Please try again.",
      });
    } catch {
      toastError({
        header: failureMessage,
        description: error.message || "An unexpected error occurred. Please try again.",
      });
    }
    if (options.onError) {
      options.onError(error, variables, context);
    }
  },
  ...options,
});

// ================ POST
export const usePost = (url, options = {}) => {
  const { csrf, ...mutationOpts } = options;
  return useMutation({
    mutationFn: (body) => fetchClient(url, { method: "POST", body, csrf }),
    ...baseMutationOptions(mutationOpts, "Operation successful", "Operation failed"),
  });
};

// ================ PUT
// ================ PUT (body OR params)
export const usePut = (url, options = {}) =>
  useMutation({
    mutationFn: (payload) => {
      if (payload?.params) {
        return fetchClient(url, {
          method: "PUT",
          params: payload.params,
          body: payload.body, 
        });
      }


      return fetchClient(url, {
        method: "PUT",
        body: payload,
      });
    },
    ...baseMutationOptions(options, "Update successful", "Update failed"),
  });


// ================ PATCH
export const usePatch = (url, options = {}) =>
  useMutation({
    mutationFn: (body) => fetchClient(url, { method: "PATCH", body }),
    ...baseMutationOptions(options, "Update successful", "Update failed"),
  });

// ================ DELETE
export const useDelete = (url, options = {}) =>
  useMutation({
    mutationFn: (idOrParams) => {
      if (idOrParams?.params) {
        return fetchClient(url, {
          method: "DELETE",
          params: idOrParams.params,
          body: idOrParams.body,
        });
      }

      return fetchClient(url, {
        method: "DELETE",
        body: idOrParams,
      });
    },
    ...baseMutationOptions(options, "Delete successful", "Delete failed"),
  });
